export type LessonBlock =
  | {
      id: string;
      type: 'heading';
      level: 2 | 3 | 4;
      text: string;
    }
  | {
      id: string;
      type: 'text';
      style?: 'intro' | 'body' | 'note';
      text: string;
    }
  | {
      id: string;
      type: 'bullets';
      items: string[];
    }
  | {
      id: string;
      type: 'numbered';
      items: string[];
    }
  | {
      id: string;
      type: 'quote';
      text: string;
      author?: string;
    }
  | {
      id: string;
      type: 'image';
      url: string;
      alt?: string;
      caption?: string;
    }
  | {
      id: string;
      type: 'video';
      url: string;
      title?: string;
    }
  | {
      id: string;
      type: 'quiz';
      /** New shape: multiple questions per block */
      questions?: Array<{
        id: string;
        question: string;
        options: { id: string; text: string; correct: boolean }[];
        explanation?: string;
      }>;
      /** Legacy shape: single question (normalized to questions[0] when reading) */
      question?: string;
      options?: { id: string; text: string; correct: boolean }[];
      explanation?: string;
    }
  | {
    id: string;
    type: 'homework';
    title?: string;
    instructions: string;
    submissionType?: 'text' | 'file' | 'link';
  };

export type LessonBlockInput = Omit<LessonBlock, 'id'> & { id?: string };

function newBlockId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `block-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createQuizQuestion(): QuizQuestion {
  return {
    id: newBlockId(),
    question: '',
    options: [
      { id: newBlockId(), text: '', correct: false },
      { id: newBlockId(), text: '', correct: true },
    ],
    explanation: '',
  };
}

export type LessonBlockType = LessonBlock['type'];

/** One quiz question (for edit/display) */
export type QuizQuestion = {
  id: string;
  question: string;
  options: { id: string; text: string; correct: boolean }[];
  explanation?: string;
};

/** Normalize quiz block to always have a questions array (supports legacy single question) */
export function getQuizQuestions(block: Extract<LessonBlock, { type: 'quiz' }>): QuizQuestion[] {
  if (block.questions && block.questions.length > 0) return block.questions;
  if (block.question != null) {
    return [{
      id: block.id + '-q0',
      question: block.question,
      options: block.options ?? [],
      explanation: block.explanation,
    }];
  }
  return [];
}

/** Return a quiz block that only has the questions array (for persisting after edit) */
export function normalizeQuizBlockForSave(block: Extract<LessonBlock, { type: 'quiz' }>): Extract<LessonBlock, { type: 'quiz' }> {
  const questions = getQuizQuestions(block);
  return { id: block.id, type: 'quiz', questions };
}

export function createBlock(type: LessonBlockType): LessonBlock {
  const id = newBlockId();
  switch (type) {
    case 'heading':
      return { id, type: 'heading', level: 2, text: '' };
    case 'text':
      return { id, type: 'text', text: '' };
    case 'bullets':
      return { id, type: 'bullets', items: [''] };
    case 'numbered':
      return { id, type: 'numbered', items: [''] };
    case 'quote':
      return { id, type: 'quote', text: '' };
    case 'image':
      return { id, type: 'image', url: '', alt: '', caption: '' };
    case 'video':
      return { id, type: 'video', url: '', title: '' };
    case 'quiz':
      return {
        id,
        type: 'quiz',
        questions: [
          {
            id: newBlockId(),
            question: '',
            options: [
              { id: newBlockId(), text: '', correct: false },
              { id: newBlockId(), text: '', correct: true },
            ],
            explanation: '',
          },
        ],
      };
    case 'homework':
      return { id, type: 'homework', title: 'Homework', instructions: '', submissionType: 'text' };
    default:
      return { id, type: 'text', text: '' };
  }
}

